import { z } from 'zod';

// URL-persisted state of the card generator form (/cards?…). Every field is
// optional and `.catch(undefined)` so a hand-edited or stale shared URL never
// crashes the page — a malformed param is silently dropped. Only what can be
// expressed as text is persisted; uploaded files (custom SVG, CSV) cannot be.

/** One optional string search param; malformed input is dropped, not fatal. */
const param = z.string().optional().catch(undefined);

export const cardsSearchSchema = z.object({
  /**
   * A pre-flight batch to resume (its `resolveBatchId`). Written when a
   * resolve is enqueued so a refresh mid-flow re-enters it: the recap when the
   * batch finished pre-flight, the live stream otherwise.
   */
  resume: param,
  /**
   * A render batch to re-attach to (its render `jobId`). Written at commit —
   * takes precedence over `resume`, so a refresh during (or after) generation
   * lands back on the run & download step: the live stream while rendering,
   * the replayed results + download once done.
   */
  job: param,
  /** Saved template id. */
  template: param,
  /** Output format; omitted when 'pdf' (the default). */
  format: z.enum(['zip', 'pdf']).optional().catch(undefined),
  /** Card sides to generate; omitted when 'front' (the default). */
  sides: z.enum(['both', 'front', 'back']).optional().catch(undefined),
  /** Field the cards are ordered by; omitted when the order is left alone. */
  sortBy: param,
  // ISEE picker: the node picked in the sessions tree.
  school: param,
  program: param,
  /** `<current section> <promo>`, as the sessions tree names it. */
  cohort: param,
  /** Omitted = every campus of the cohort. */
  campus: param,
  /** Comma-separated admission statuses ticked; omitted = all of them. */
  admStatus: param,
  /** Comma-separated sections of the cohort ticked; omitted = all of them. */
  section: param,
  /** Comma-separated specialities ticked; omitted = no filter. */
  speciality: param,
  /** `form:` field values, one per template form field. */
  values: z.record(z.string(), z.string()).optional().catch(undefined),
  /** Lucca employee logins. */
  logins: z.array(z.string()).optional().catch(undefined),
});

export type CardsSearch = z.infer<typeof cardsSearchSchema>;

/** Patch merged into the URL search params; an `undefined` value removes its key. */
export type CardsSearchPatch = Partial<CardsSearch>;

/** The subset of the URL state owned by the ISEE student picker. */
export type IseeQueryState = Pick<
  CardsSearchPatch,
  | 'school'
  | 'program'
  | 'cohort'
  | 'campus'
  | 'admStatus'
  | 'section'
  | 'speciality'
>;

/** Extract the ISEE picker's subset from the full URL state. */
export function pickIseeQuery(search?: CardsSearch): IseeQueryState {
  return {
    school: search?.school,
    program: search?.program,
    cohort: search?.cohort,
    campus: search?.campus,
    admStatus: search?.admStatus,
    section: search?.section,
    speciality: search?.speciality,
  };
}

/** A patch that clears every persisted form param (used by the form reset). */
export const CARDS_SEARCH_CLEAR: CardsSearchPatch = Object.fromEntries(
  Object.keys(cardsSearchSchema.shape).map((k) => [k, undefined]),
);
